import { ClosingSequence, type ClosingStep } from './closing-sequence';
import type { LevelId, TestLevelId } from '../data/ids';

/**
 * Screen state machine (GDD §13.2). Pure: no DOM, no Three.js. It never decides game rules: the level
 * state machine (src/core/level-state.ts) does, and `onLevelState` only reacts to it.
 *
 *   ARRANQUE > AVISO > TITULO > EXPEDIENTES > MISIONES > BRIEFING > VUELO <> PAUSA
 *                                    ^   ^                  |   VUELO > OPERACION_FALLIDA > BRIEFING
 *                                    |   +-- RECUENTO <-----+   VUELO > RECUENTO > MISIONES (CIERRE after OP-403)
 *                                    +-- AJUSTES (back to the screen that opened it)
 */
export type ScreenId =
  | 'ARRANQUE'
  | 'AVISO'
  | 'TITULO'
  | 'EXPEDIENTES'
  | 'MISIONES'
  | 'AJUSTES'
  | 'BRIEFING'
  | 'VUELO'
  | 'PAUSA'
  | 'OPERACION_FALLIDA'
  | 'RECUENTO'
  | 'CIERRE';

export type ScreenEvent =
  | 'ASSETS_LOADED'
  | 'CONTINUE'
  | 'START'
  | 'SLOT_READY'
  | 'SELECT_MISSION'
  | 'OPEN_SETTINGS'
  | 'BACK'
  | 'DEPLOY'
  | 'PAUSE'
  | 'RESUME'
  | 'RESTART_CHECKPOINT'
  | 'ABANDON'
  | 'GAME_OVER'
  | 'LEVEL_COMPLETED'
  | 'RETRY'
  | 'OPEN_CREDITS';

export const SCREENS: readonly ScreenId[] = [
  'ARRANQUE',
  'AVISO',
  'TITULO',
  'EXPEDIENTES',
  'MISIONES',
  'AJUSTES',
  'BRIEFING',
  'VUELO',
  'PAUSA',
  'OPERACION_FALLIDA',
  'RECUENTO',
  'CIERRE',
];

export const SCREEN_EVENTS: readonly ScreenEvent[] = [
  'ASSETS_LOADED',
  'CONTINUE',
  'START',
  'SLOT_READY',
  'SELECT_MISSION',
  'OPEN_SETTINGS',
  'BACK',
  'DEPLOY',
  'PAUSE',
  'RESUME',
  'RESTART_CHECKPOINT',
  'ABANDON',
  'GAME_OVER',
  'LEVEL_COMPLETED',
  'RETRY',
  'OPEN_CREDITS',
];

/**
 * The exits of GDD §13.2. Two exits depend on the context and are resolved in `send`: AJUSTES > BACK goes
 * back to the screen that opened it, and RECUENTO > CONTINUE goes to CIERRE after OP-403.
 */
export const SCREEN_TRANSITIONS: Readonly<Record<ScreenId, Partial<Record<ScreenEvent, ScreenId>>>> = {
  ARRANQUE: { ASSETS_LOADED: 'AVISO' },
  AVISO: { CONTINUE: 'TITULO' },
  TITULO: { START: 'EXPEDIENTES' },
  EXPEDIENTES: { SLOT_READY: 'MISIONES' },
  MISIONES: { SELECT_MISSION: 'BRIEFING', OPEN_SETTINGS: 'AJUSTES', OPEN_CREDITS: 'CIERRE' },
  AJUSTES: { BACK: 'MISIONES' },
  BRIEFING: { DEPLOY: 'VUELO', BACK: 'MISIONES' },
  VUELO: { PAUSE: 'PAUSA', GAME_OVER: 'OPERACION_FALLIDA', LEVEL_COMPLETED: 'RECUENTO' },
  PAUSA: { RESUME: 'VUELO', RESTART_CHECKPOINT: 'VUELO', ABANDON: 'MISIONES', OPEN_SETTINGS: 'AJUSTES' },
  OPERACION_FALLIDA: { RETRY: 'BRIEFING' },
  RECUENTO: { CONTINUE: 'MISIONES' },
  CIERRE: { CONTINUE: 'TITULO' },
};

const LAST_LEVEL: LevelId = '4-3';

export type ScreenListener = (screen: ScreenId) => void;

export interface ScreenContext {
  /** The mission on BRIEFING, VUELO and RECUENTO. */
  levelId?: LevelId | TestLevelId | null;
}

export class ScreenFlow {
  #current: ScreenId;
  #levelId: LevelId | TestLevelId | null;
  /** The screen that opened AJUSTES, where BACK returns. */
  #settingsOrigin: ScreenId = 'MISIONES';
  readonly #listeners = new Set<ScreenListener>();
  /** The closing sequence of CIERRE (#136): created on entering it, from its first beat or from the credits (MISIONES). */
  #closing: ClosingSequence | null = null;
  #closingStart: ClosingStep = 'REVELACION';

  constructor(start: ScreenId = 'ARRANQUE', context: ScreenContext = {}) {
    this.#current = start;
    this.#levelId = context.levelId ?? null;
  }

  get current(): ScreenId {
    return this.#current;
  }

  get levelId(): LevelId | TestLevelId | null {
    return this.#levelId;
  }

  /** The closing sequence while CIERRE is the current screen, else null. */
  get closing(): ClosingSequence | null {
    return this.#closing;
  }

  /**
   * Applies an event of §13.2. Returns false, without notifying, when the current screen has no such
   * exit (unknown events included: they may arrive from the test hooks at run time).
   */
  send(event: ScreenEvent | string, context: ScreenContext = {}): boolean {
    let next = SCREEN_TRANSITIONS[this.#current][event as ScreenEvent];
    if (!next) return false;
    if (this.#current === 'AJUSTES' && event === 'BACK') next = this.#settingsOrigin;
    if (this.#current === 'RECUENTO' && event === 'CONTINUE' && this.#levelId === LAST_LEVEL) next = 'CIERRE';
    if (next === 'AJUSTES') this.#settingsOrigin = this.#current;
    // The credits opened from MISIONES start at CREDITOS; after the last level the sequence starts at its first beat.
    this.#closingStart = event === 'OPEN_CREDITS' ? 'CREDITOS' : 'REVELACION';
    if (context.levelId !== undefined) this.#levelId = context.levelId;
    this.#change(next);
    return true;
  }

  /** AVISO > TITULO, from the "Continuar" button. */
  continue(): ScreenId {
    this.send('CONTINUE');
    return this.#current;
  }

  /**
   * Reacts to the level state machine (#50) while flying: OPERACION_FALLIDA and COMPLETADO leave VUELO;
   * DERRIBO and REAPARICION stay on VUELO (GDD §13.2: DERRIBO > REAPARECER > VUELO).
   */
  onLevelState(state: string): boolean {
    if (state === 'OPERACION_FALLIDA') return this.send('GAME_OVER');
    if (state === 'COMPLETADO') return this.send('LEVEL_COMPLETED');
    return false;
  }

  /** Direct jump for loading a level or a save outside the normal flow (test hooks, #51). */
  goTo(screen: ScreenId, context: ScreenContext & { closingStart?: ClosingStep } = {}): ScreenId {
    if (context.levelId !== undefined) this.#levelId = context.levelId;
    this.#closingStart = context.closingStart ?? 'REVELACION';
    // Opening CIERRE again (a test hook) starts a fresh sequence on the same screen.
    if (screen === 'CIERRE' && this.#current === 'CIERRE') {
      this.#closing = new ClosingSequence(this.#closingStart);
      this.refresh();
      return this.#current;
    }
    this.#change(screen);
    return this.#current;
  }

  /** Notifies the current screen again, for content that changed under it (a save loaded by #51). */
  refresh(): void {
    for (const listener of this.#listeners) listener(this.#current);
  }

  subscribe(listener: ScreenListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #change(next: ScreenId): void {
    if (next === this.#current) return;
    this.#current = next;
    this.#closing = next === 'CIERRE' ? new ClosingSequence(this.#closingStart) : null;
    for (const listener of this.#listeners) listener(next);
  }
}
